import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Grow } from '@mui/material';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDuplicateCart } from 'src/hooks';
import DuplicateIcon from 'src/images/icons/duplicate-icon.svg?react';
import EditIcon from 'src/images/icons/edit-icon.svg?react';
import EmbedIcon from 'src/images/icons/embed.svg?react';
import ExportIcon from 'src/images/icons/export.svg?react';
import GetListIcon from 'src/images/icons/get-list.svg?react';
import HelpIcon from 'src/images/icons/help.svg?react';
import LockIcon from 'src/images/icons/password.svg?react';
import { showEmbedModalAtom } from 'src/store';
import type { CartGetItem } from 'src/types';
import { exportCartToExcel } from 'src/utils';

type Props = {
  cartId: string;
  cartCCY?: string;
  cartCCYS?: string;
  cartTitle?: string | undefined;
  cartItems?: CartGetItem[];
  cartTimestamp?: number;
  cartStore?: string | undefined;
  isLoading: boolean;
  // 'make' swaps Edit for Get List and hides Password (both live on the page).
  page?: 'get' | 'make';
  // Overrides for pages whose duplicate/export must act on live page state.
  onDuplicate?: () => void;
  onExport?: () => void;
};

export const ListActions = ({
  cartId,
  cartCCY,
  cartCCYS,
  cartTitle,
  cartItems,
  cartTimestamp,
  cartStore,
  isLoading,
  page = 'get',
  onDuplicate,
  onExport,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [animationDone, setAnimationDone] = useState<boolean>(true);
  const [, setShowEmbedModal] = useAtom<boolean>(showEmbedModalAtom);

  const { t } = useTranslation();

  // Empty cartId disables the hook's internal cart fetch when the duplicate
  // action is overridden.
  const duplicateCart = useDuplicateCart({ cartId: onDuplicate ? '' : cartId });

  const handleClick = () => {
    if (!open) {
      setAnimationDone(false);
      return setOpen(true);
    }

    setTimeout(() => {
      setAnimationDone(true);
    }, 150);
    setOpen(false);
  };

  const exportCartGetData = () => {
    if (onExport) return onExport();
    exportCartToExcel(
      cartId,
      cartCCY ?? 'USD',
      cartCCYS ?? '$',
      cartItems ?? [],
      cartTitle,
      cartTimestamp ?? 0,
      cartStore,
    );
  };

  const buttonsDisabled = isLoading || duplicateCart.isPending;

  return (
    <>
      <ul className='z-[1] flex flex-row flex-wrap items-start justify-start gap-3'>
        <button
          disabled={buttonsDisabled}
          onClick={handleClick}
          className={`button-secondary flex h-12 flex-row items-center justify-center gap-2 transition-all ${!animationDone ? 'w-12 p-0' : ''}`}
        >
          {!animationDone ? (
            <CloseIcon className='text-primary' style={{ fontSize: 24 }} />
          ) : (
            <>
              <SettingsOutlinedIcon
                className='text-2xl text-primary'
                style={{ fontSize: 24 }}
              />
              {t('common.list_actions')}
            </>
          )}
        </button>
        <Grow in={open} unmountOnExit {...(open ? { timeout: 150 } : {})}>
          <li>
            <Link
              to={page === 'make' ? '/guide/creating-a-list' : '/guide/loading-a-list'}
              reloadDocument
            >
              <button className='button-secondary flex h-12 flex-row items-center justify-center gap-2'>
                <div className='text-primary'>
                  <HelpIcon />
                </div>
                {t('common.help')}
              </button>
            </Link>
          </li>
        </Grow>

        <Grow in={open} unmountOnExit {...(open ? { timeout: 350 } : {})}>
          <li>
            {page === 'make' ? (
              <Link to={`/get/${cartId}`} reloadDocument>
                <button className='button-secondary flex h-12 flex-row items-center justify-center gap-2'>
                  <div className='text-primary'>
                    <GetListIcon />
                  </div>
                  {t('common.get_list')}
                </button>
              </Link>
            ) : (
              <Link to={`/make/${cartId}`} reloadDocument>
                <button className='button-secondary flex h-12 flex-row items-center justify-center gap-2'>
                  <EditIcon className='text-primary' />
                  {t('common.edit')}
                </button>
              </Link>
            )}
          </li>
        </Grow>

        <Grow in={open} unmountOnExit {...(open ? { timeout: 550 } : {})}>
          <li>
            <Link
              to={`/share/${cartId}`}
              reloadDocument
            >
              <button className='button-secondary flex h-12 flex-row items-center justify-center gap-2'>
                <ShareOutlinedIcon className='text-primary' />
                {t('common.share')}
              </button>
            </Link>
          </li>
        </Grow>

        <Grow in={open} unmountOnExit {...(open ? { timeout: 550 } : {})}>
          <li>
            <button
              className='button-secondary flex h-12 flex-row items-center justify-center gap-2'
              onClick={() => (onDuplicate ? onDuplicate() : duplicateCart.mutate())}
              disabled={buttonsDisabled}
            >
              <DuplicateIcon className='text-primary' />
              {t('common.duplicate')}
            </button>
          </li>
        </Grow>

        <Grow in={open} unmountOnExit {...(open ? { timeout: 750 } : {})}>
          <li>
            <button
              className='button-secondary flex h-12 flex-row items-center justify-center gap-2'
              onClick={() => setShowEmbedModal(true)}
              disabled={buttonsDisabled}
            >
              <div className='text-primary'>
                <EmbedIcon />
              </div>
              {t('common.embed')}
            </button>
          </li>
        </Grow>

        {page !== 'make' && (
          <Grow in={open} unmountOnExit {...(open ? { timeout: 950 } : {})}>
            <li>
              <Link
                to={`/make/${cartId}#password`}
                reloadDocument
              >
                <button
                  className='button-secondary flex h-12 flex-row items-center justify-center gap-2'
                  disabled={buttonsDisabled}
                >
                  <div className='text-primary'>
                    <LockIcon />
                  </div>
                  {t('common.password')}
                </button>
              </Link>
            </li>
          </Grow>
        )}

        <Grow in={open} unmountOnExit {...(open ? { timeout: 1150 } : {})}>
          <li>
            <button
              onClick={exportCartGetData}
              className='button-secondary flex h-12 flex-row items-center justify-center gap-2'
            >
              <div className='text-primary'>
                <ExportIcon />
              </div>
              {t('common.export')}
            </button>
          </li>
        </Grow>
      </ul>
    </>
  );
};
